import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X, MoreVertical } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
let deferred = null;
const subs = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; subs.forEach((f) => f()); });
  window.addEventListener("appinstalled", () => { deferred = null; subs.forEach((f) => f()); });
}

export function useAppManifest({ kind, slug = "", name = "" }) {
  useEffect(() => {
    const qs = new URLSearchParams({ kind, slug, name }).toString();
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.href = `${API}/api/public/manifest?${qs}`;
    const title = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (title) title.content = kind === "agent" ? `XALTIX ${name || "Agent"}` : "XALTIX";
  }, [kind, slug, name]);
}

const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const IosHelp = ({ onClose }) => (
  <div className="fixed inset-0 z-[80] bg-black/60 grid place-items-end sm:place-items-center p-4" data-testid="install-ios-help">
    <div className="w-full max-w-sm bg-white text-slate-900 rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3"><b className="font-display text-lg">Installer XALTIX</b><button onClick={onClose} aria-label="Fermer" data-testid="install-ios-close"><X className="h-5 w-5" /></button></div>
      {isIos() ? (
        <ol className="space-y-2 text-sm">
          <li className="flex items-center gap-2">1. Touchez <Share className="h-4 w-4 text-[#0B2A7A]" /> <b>Partager</b> dans Safari</li>
          <li className="flex items-center gap-2">2. Choisissez <PlusSquare className="h-4 w-4 text-[#0B2A7A]" /> <b>Sur l'écran d'accueil</b></li>
          <li>3. Touchez <b>Ajouter</b> : l'icône XALTIX apparaît sur votre écran.</li>
        </ol>
      ) : (
        <ol className="space-y-2 text-sm" data-testid="install-android-steps">
          <li>1. Ouvrez cette page dans <b>Chrome</b> (pas depuis l'application Agent)</li>
          <li className="flex items-center gap-2">2. Touchez <MoreVertical className="h-4 w-4 text-[#0B2A7A]" /> en haut à droite</li>
          <li>3. Choisissez <b>Installer l'application</b> ou <b>Ajouter à l'écran d'accueil</b>, puis <b>Installer</b>.</li>
          <li className="text-xs text-slate-500">L'application Admin s'installe à côté de l'application Agent : deux icônes distinctes.</li>
        </ol>
      )}
    </div>
  </div>
);

export const InstallAppButton = ({ dark = false, className = "" }) => {
  const [, force] = useState(0);
  const [help, setHelp] = useState(false);
  useEffect(() => { const f = () => force((n) => n + 1); subs.add(f); return () => subs.delete(f); }, []);
  if (standalone() || (!deferred && !isIos() && !/android/i.test(navigator.userAgent))) return null;
  const click = async () => {
    if (deferred) { deferred.prompt(); await deferred.userChoice.catch(() => {}); deferred = null; force((n) => n + 1); }
    else setHelp(true);
  };
  const cls = dark ? "border-[#E8C46A]/60 text-white bg-white/5 hover:bg-[#E8C46A]/15" : "border-[#C9962E] text-[#0B2A7A] bg-white hover:bg-amber-50";
  return (
    <>
      <button type="button" onClick={click} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${cls} ${className}`} data-testid="install-app-btn">
        <Download className="h-3.5 w-3.5" /> Installer l'application
      </button>
      {help && <IosHelp onClose={() => setHelp(false)} />}
    </>
  );
};
